import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import {
  useEmployee, useUpdateEmployee, useResetEmployeePassword, useCompanyOptions, useRoleOptions,
  useDepartments, usePositions, useStates, useStateCountries, errMessage, type EmployeeDetail,
} from './systemMasters';
import { useCities } from './city';
import { useContentRectStyle } from './useContentRectStyle';
import { RichTextEditor } from './RichTextEditor';
import { LookupSelect } from './LookupSelect';

const GENDERS = [{ v: 0, t: 'None' }, { v: 1, t: 'Male' }, { v: 2, t: 'Female' }];
const LANGUAGES = [{ v: 0, t: 'Chinese' }, { v: 1, t: 'English' }];

const BLANK: EmployeeDetail = {
  id: '', displayName: '', userName: '', isActive: true, changePasswordOnFirstLogon: false,
  firstName: '', lastName: '', initial: '', jobTitle: '', gender: null, birthday: null,
  email: '', mobilePhone: '', officePhone: '', homePhone: '', fax: '', otherPhone: '', address: '', zip: '',
  company: null, department: null, position: null, role: null, country: null, state: null, city: null,
  employmentDate: null, retireDate: null, language: null, isEnabledLanguage: false, isManager: false, isLabUser: false,
  isLIMSAdministrator: false, resume: null, photo: null, signature: null, deliveryPassword: null,
};

type EmpTab = 'basic' | 'org' | 'contact' | 'resume' | 'user';

/**
 * The full Employee detail/edit form (opened from the Department ▸ Employees tab
 * "Edit" pencil) — mirrors the legacy XAF Employee_DetailView: Identity & Login,
 * Personal & Contact, and Job sections. Employee is a TPT user; login fields write
 * to PermissionPolicyUser, HR fields to Employee (handled server-side).
 */
export function EmployeeFormDialog({ employeeId, onClose, onSaved }: { employeeId: string; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const { data: loaded } = useEmployee(employeeId);
  const update = useUpdateEmployee();
  const { data: companies = [] } = useCompanyOptions();
  const { data: roles = [] } = useRoleOptions();
  const { data: departments = [] } = useDepartments();
  const { data: positions = [] } = usePositions();
  const { data: states = [] } = useStates();
  const { data: countries = [] } = useStateCountries();
  const { data: cities = [] } = useCities();

  const resetPwd = useResetEmployeePassword();
  const [form, setForm] = useState<EmployeeDetail>(BLANK);
  const [tab, setTab] = useState<EmpTab>('basic');
  const [imgEditing, setImgEditing] = useState<Record<string, boolean>>({});   // per-image: show Browse after clicking the edit pencil
  // Reset Password sub-dialog (LIMS: New Password + Confirm Password).
  const [pwdOpen, setPwdOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  // Nested popup → opens CENTERED (LIMS shows the Employee detail as a centred modal,
  // not full-screen); the header toggle still maximizes it.
  const [maxed, setMaxed] = useState(false);
  const maxStyle = useContentRectStyle(maxed);
  useEffect(() => { if (loaded) setForm(loaded); }, [loaded]);

  const set = (k: keyof EmployeeDetail, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const dateVal = (s: string | null) => (s ? s.slice(0, 10) : '');

  const submit = async () => {
    if (!form.firstName?.trim()) { setTab('basic'); await alert({ title: 'Validation', message: 'First Name must not be empty.', tone: 'error' }); return; }
    if (!form.lastName?.trim()) { setTab('basic'); await alert({ title: 'Validation', message: 'Last Name must not be empty.', tone: 'error' }); return; }
    if (!form.company) { setTab('org'); await alert({ title: 'Validation', message: 'Division must not be empty.', tone: 'error' }); return; }
    if (!form.email?.trim()) { setTab('user'); await alert({ title: 'Validation', message: 'Email must not be empty.', tone: 'error' }); return; }
    if (!form.displayName?.trim()) { setTab('user'); await alert({ title: 'Validation', message: 'Display Name must not be empty.', tone: 'error' }); return; }
    try {
      await update.mutateAsync(form);
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      onSaved();
    } catch (e) { notify.error(errMessage(e)); }
  };

  const openResetPwd = () => { setNewPwd(''); setConfirmPwd(''); setPwdOpen(true); };
  const doResetPwd = async () => {
    if (!newPwd) { await alert({ title: 'Validation', message: 'New Password must not be empty.', tone: 'error' }); return; }
    if (newPwd !== confirmPwd) { await alert({ title: 'Validation', message: 'Passwords do not match.', tone: 'error' }); return; }
    try {
      await resetPwd.mutateAsync({ id: employeeId, newPassword: newPwd, confirmPassword: confirmPwd });
      setPwdOpen(false);
      await alert({ title: 'Success', message: 'Password reset.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };

  // Render helpers (plain functions, NOT components, so inputs keep focus on edit).
  const text = (label: string, k: keyof EmployeeDetail, req = false) => (
    <div className="acc-field-h"><label className={`acc-label${req ? ' acc-label-req' : ''}`}>{label}{req && <span className="req">*</span>}</label>
      <div className="acc-fh-control"><input className="acc-input" value={(form[k] as string | null) ?? ''} onChange={(e) => set(k, e.target.value)} /></div>
    </div>
  );
  // Multi-line text field (LIMS renders Mobile Phone as a tall memo box).
  const textMulti = (label: string, k: keyof EmployeeDetail, rows = 3) => (
    <div className="acc-field-h acc-field-tall"><label className="acc-label">{label}</label>
      <div className="acc-fh-control"><textarea className="acc-input acc-text-multi" rows={rows} value={(form[k] as string | null) ?? ''} onChange={(e) => set(k, e.target.value)} /></div>
    </div>
  );
  const date = (label: string, k: keyof EmployeeDetail) => {
    const v = dateVal(form[k] as string | null);
    return (
      <div className="acc-field-h"><label className="acc-label">{label}</label>
        {/* acc-date-empty hides the native mm/dd/yyyy placeholder until focused/filled */}
        <div className="acc-fh-control"><input className={`acc-input acc-date${v ? '' : ' acc-date-empty'}`} type="date" value={v} onChange={(e) => set(k, e.target.value || null)} /></div>
      </div>
    );
  };
  // Checkbox + label together (LIMS User Information tab — tightly stacked under the control column).
  const checkInline = (label: string, k: keyof EmployeeDetail) => (
    <label className="acc-check-inline"><input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} /> {label}</label>
  );
  const sel = (label: string, k: keyof EmployeeDetail, opts: { id: string; label: string }[], req = false) => {
    const cur = form[k] as string | null;
    return (
      <div className="acc-field-h"><label className={`acc-label${req ? ' acc-label-req' : ''}`}>{label}{req && <span className="req">*</span>}</label>
        <div className="acc-fh-control">
          {/* Bounded lookup dropdown (scrollable + search) + erase icon — LIMS XAF lookup fields. */}
          <div className="acc-lookup">
            <LookupSelect value={cur} options={opts} onChange={(v) => set(k, v)} />
            <button type="button" className="acc-lookup-btn" title="Erase" disabled={!cur} onClick={() => set(k, null)}><i className="ti ti-eraser" /></button>
          </div>
        </div>
      </div>
    );
  };
  const radio = (label: string, k: keyof EmployeeDetail, opts: { v: number; t: string }[], req = false) => (
    <div className="acc-field-h"><label className={`acc-label${req ? ' acc-label-req' : ''}`}>{label}{req && <span className="req">*</span>}</label>
      <div className="acc-fh-control acc-radio-row">
        {opts.map((o) => (
          <label key={o.v} className="acc-radio"><input type="radio" name={String(k)} checked={form[k] === o.v} onChange={() => set(k, o.v)} /> {o.t}</label>
        ))}
      </div>
    </div>
  );
  const pickImage = (k: keyof EmployeeDetail, file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const r = String(reader.result); set(k, r.includes(',') ? r.split(',')[1] : r); };  // strip the data: prefix → raw base64
    reader.readAsDataURL(file);
    setImgEditing((s) => ({ ...s, [k]: false }));   // hide Browse again once a file is chosen
  };
  const browse = (k: keyof EmployeeDetail, label = 'Browse…') => (
    <label className="ca-btn acc-img-browse">{label}
      <input type="file" accept="image/*" hidden onChange={(e) => pickImage(k, e.target.files?.[0])} />
    </label>
  );
  // LIMS image field: empty → Browse only; uploaded → image + edit/erase icons, and
  // the Browse button appears only AFTER clicking the edit pencil.
  const imageField = (label: string, k: keyof EmployeeDetail) => {
    const b64 = form[k] as string | null;
    const editing = imgEditing[String(k)] ?? false;
    return (
      <div className="acc-img-field">
        <label className="acc-label">{label}</label>
        {b64 ? (
          <>
            <div className="acc-img-box"><img src={`data:image/png;base64,${b64}`} alt={label} /></div>
            <div className="acc-img-acts">
              <button type="button" className="pts-icon-btn edit" title="Edit" onClick={() => setImgEditing((s) => ({ ...s, [String(k)]: true }))}><i className="ti ti-edit" /></button>
              <button type="button" className="pts-icon-btn" title="Erase" onClick={() => { set(k, null); setImgEditing((s) => ({ ...s, [String(k)]: false })); }}><i className="ti ti-eraser" /></button>
            </div>
            {editing && <div className="acc-img-acts">{browse(k)}</div>}
          </>
        ) : (
          <>
            {/* Empty image placeholder box (photo space) above the Browse button. */}
            <div className="acc-img-box acc-img-box-empty"><i className="ti ti-photo acc-img-ph" /></div>
            <div className="acc-img-acts">{browse(k)}</div>
          </>
        )}
      </div>
    );
  };

  const companyOpts = companies.map((c) => ({ id: c.id, label: c.name }));
  const deptOpts = departments.map((d) => ({ id: d.id, label: d.name ?? '' }));
  const posOpts = positions.map((p) => ({ id: p.id, label: p.positionName ?? '' }));
  const roleOpts = roles.map((r) => ({ id: r.id, label: r.name }));
  const countryOpts = countries.map((c) => ({ id: c.id, label: c.name }));
  const stateOpts = states.map((s) => ({ id: s.id, label: s.longName ?? s.shortName ?? '' }));
  const cityOpts = cities.map((c) => ({ id: c.id, label: c.cityName ?? '' }));

  return (
    <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); onClose(); }}>
      <div className={`acc-dialog acc-dialog-lg acc-dialog-emp${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div className="acc-dialog-hdr">
          <span>{form.displayName ? `${form.displayName} - Employee` : 'Employee'}</span>
          <span className="acc-dialog-acts">
            <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
            <i className="ti ti-x acc-dialog-x" onClick={onClose} />
          </span>
        </div>
        <div className="acc-dialog-body acc-compact">
          <div className="acc-tabs">
            <button className={`acc-tab${tab === 'basic' ? ' active' : ''}`} onClick={() => setTab('basic')}>Basic Information</button>
            <button className={`acc-tab${tab === 'org' ? ' active' : ''}`} onClick={() => setTab('org')}>Organization</button>
            <button className={`acc-tab${tab === 'contact' ? ' active' : ''}`} onClick={() => setTab('contact')}>Contact</button>
            <button className={`acc-tab${tab === 'resume' ? ' active' : ''}`} onClick={() => setTab('resume')}>Resume</button>
            <button className={`acc-tab${tab === 'user' ? ' active' : ''}`} onClick={() => setTab('user')}>User Information</button>
          </div>

          {tab === 'basic' && (
            <div className="acc-tab-pane acc-emp-basic">
              <div className="acc-emp-col">
                {text('First Name', 'firstName', true)}
                {text('Last Name', 'lastName', true)}
                {text('Initial', 'initial')}
                {text('Job Title', 'jobTitle')}
                {radio('Gender', 'gender', GENDERS)}
                {date('Birthday', 'birthday')}
              </div>
              <div className="acc-emp-imgs">
                {imageField('Photo', 'photo')}
                {imageField('Signature', 'signature')}
              </div>
            </div>
          )}

          {tab === 'org' && (
            <div className="acc-tab-pane">
              {sel('Division', 'company', companyOpts, true)}
              {sel('Department', 'department', deptOpts)}
              {sel('Position', 'position', posOpts)}
              {date('Employment Date', 'employmentDate')}
              {date('Retire Date', 'retireDate')}
            </div>
          )}

          {tab === 'contact' && (
            // Two parallel columns (LIMS Contact tab): phones/lookups split left & right.
            <div className="acc-tab-pane acc-emp-2col">
              <div className="acc-emp-col">
                {text('Office Phone', 'officePhone')}
                {textMulti('Mobile Phone', 'mobilePhone')}
                {text('Other Phone', 'otherPhone')}
                {sel('Country', 'country', countryOpts)}
                {text('Zip', 'zip')}
                {text('Address', 'address')}
              </div>
              <div className="acc-emp-col">
                {text('Home Phone', 'homePhone')}
                {text('Fax', 'fax')}
                {text('Delivery Email', 'email', true)}
                {text('Delivery Password', 'deliveryPassword')}
                {sel('State', 'state', stateOpts)}
                {sel('City', 'city', cityOpts)}
              </div>
            </div>
          )}

          {tab === 'resume' && (
            <div className="acc-tab-pane">
              {/* No left "Resume" caption — the editor fills the tab (LIMS). */}
              <RichTextEditor value={form.resume ?? ''} onChange={(html) => set('resume', html)} />
            </div>
          )}

          {tab === 'user' && (
            <div className="acc-tab-pane">
              {text('Email', 'email', true)}
              {text('Display Name', 'displayName', true)}
              {/* Password row: a "set" indicator + Reset Password (actual reset is handled in User & Security). */}
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">Password<span className="req">*</span></label>
                <div className="acc-fh-control acc-pwd-row">
                  <input type="checkbox" checked readOnly disabled />
                  <button type="button" className="ca-btn" onClick={openResetPwd}>Reset Password</button>
                </div>
              </div>
              {sel('Role', 'role', roleOpts)}
              {/* Tightly-stacked checkbox group aligned under the control column (LIMS). */}
              <div className="acc-field-h acc-field-tall">
                <label className="acc-label" />
                <div className="acc-fh-control acc-checks-col">
                  {checkInline('Is Lab User', 'isLabUser')}
                  {checkInline('Is LIMS Administrator', 'isLIMSAdministrator')}
                  {checkInline('Is Active', 'isActive')}
                  {checkInline('Is Enabled Language', 'isEnabledLanguage')}
                </div>
              </div>
              {radio('Language', 'language', LANGUAGES)}
            </div>
          )}
        </div>
        <div className="acc-dialog-ftr">
          <button className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={update.isPending}><span className="tb-i">💾</span> {update.isPending ? 'Saving…' : 'OK'}</button>
          <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        </div>
      </div>

      {/* Reset Password sub-dialog (LIMS) — portaled to body, above the Employee dialog. */}
      {pwdOpen && createPortal(
        <div className="acc-overlay" style={{ zIndex: 1300 }} onMouseDown={(e) => { e.stopPropagation(); setPwdOpen(false); }}>
          <div className="acc-dialog" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Reset Password</span><i className="ti ti-x acc-dialog-x" onClick={() => setPwdOpen(false)} /></div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">New Password<span className="req">*</span></label>
                <div className="acc-fh-control"><input className="acc-input" type="password" autoFocus value={newPwd} onChange={(e) => setNewPwd(e.target.value)} /></div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">Confirm Password<span className="req">*</span></label>
                <div className="acc-fh-control"><input className="acc-input" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void doResetPwd(); }} /></div>
              </div>
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void doResetPwd()} disabled={resetPwd.isPending}><span className="tb-i">💾</span> {resetPwd.isPending ? 'Saving…' : 'OK'}</button>
              <button className="ca-btn" onClick={() => setPwdOpen(false)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
